import React, {FunctionComponent, useState, Component, MouseEvent, useEffect} from "react";
import {Modal, Dimmer, Loader, Form, Message, Button, Segment, Dropdown, Header, Input, Table, Icon, Divider} from "semantic-ui-react";
import {DateInput} from "semantic-ui-calendar-react";
import DeleteDialog from "../components/deleteDialog";
import {IItemDialogProps, IItemsListState, calcPagination, DialogType} from './globals';

interface IDisk {
    id:             string,
    customer_id:    number
    customer:       string,
    app:            string,
    version:        string,
    expiration:     string,
    license:        string,
    description:    string,
    create:         number,
    update:         number
}

const DiskEditDialog: FunctionComponent<IItemDialogProps<IDisk>> = ({item, type, onClose}) => {
    const [data, setData] = useState(item);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        fetch("./customer")
            .then(res => res.ok
                ? res.json().then((json) => setCustomers(json.map((item: any) => ({text: item.name, value: item.id}))))
                : res.text().then(text => setError(text))
            )
    }, []);

    const isJSONInvalid = (str: string) => {
        try {
            JSON.parse(str)
        } catch {
            return true
        }
        return false;
    }

    return (
        <Modal
            open
            closeIcon
            size="tiny"
            icon="edit"
            onClose={_ => onClose(false)}
        >
            <Modal.Header>{`${type === "new" ? "Create" : "Edit"} disk`}</Modal.Header>
            <Modal.Content>
                <Dimmer active={loading} inverted>
                    <Loader>Process disk info</Loader>
                </Dimmer>
                <Form>
                    <Form.Input readOnly label="ID" placeholder="id" value={data.id}/>
                    <Form.Group widths="equal">
                        <Form.Input readOnly fluid label="App" placeholder="app" value={data.app}/>
                        <Form.Input readOnly fluid label="Version" placeholder="version" value={data.version}/>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input readOnly fluid label="Create" placeholder="none" value={new Date(data.create).toLocaleString()}/>
                        <Form.Input readOnly fluid label="Update" placeholder="none" value={data.update ? new Date(data.update).toLocaleString() : ""}/>
                    </Form.Group>
                    <Form.Select
                        label="Customer"
                        value={data.customer_id}
                        options={customers}
                        onChange={(e: React.SyntheticEvent<HTMLElement>, eventData) => setData({
                            ...data,
                            customer_id: typeof eventData.value === "number" ? eventData.value : 1
                        })}/>
                        <DateInput animation={'fade'} dateFormat="YYYY-MM-DD" label="Expiration" value={data.expiration} onChange={(e: React.SyntheticEvent<HTMLElement>, {value}) => setData({...data, expiration: value})}/>
                    <Form.TextArea label="License" error={isJSONInvalid(data.license)} placeholder="none" value={data.license} onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setData({...data, license: e.currentTarget.value})}/>
                    <Form.TextArea label="Description" placeholder="Tell about disk..." value={data.description} onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setData({...data, description: e.currentTarget.value})}/>
                </Form>
                <Message negative hidden={error.length === 0}>
                    {error}
                </Message>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    positive
                    icon="save"
                    labelPosition="left"
                    content="Save"
                    onClick={(e: React.MouseEvent) => {
                        setLoading(true);
                        fetch("/disk", {
                            method: type === "new" ? "POST" : "PUT",
                            headers: {'content-type' : 'application/json'},
                            body: JSON.stringify({
                                id:             data.id,
                                customer_id:    data.customer_id,
                                expiration:     data.expiration,
                                license:        data.license,
                                description:    data.description
                            })
                        })
                            .then(result => result.ok
                                ? onClose(true)
                                : result.text()
                                    .then(error => setError(error)))
                            .catch(error => setError(error))
                            .finally(() => setLoading(false))
                    }}
                />
            </Modal.Actions>
        </Modal>
    )
}

export default class Disk extends Component<{}, IItemsListState<IDisk>> {
    state = {
        loading: false,
        items: [],
        page: 1,
        filter: "",
        order: "expiration",
        desc: false,
        dialog: null
    }

    orderOptions = [
        { key: 1, text: "ID", value: "id" },
        { key: 2, text: "Customer", value: "customer" },
        { key: 3, text: "Expiration", value: "expiration" },
        { key: 3, text: "Create", value: "create" },
    ]

    componentDidMount() {
        this.updateDisks();
    }

    updateDisks() {
        this.setState({loading: true});
        fetch("./disk")
            .then(res => res.ok
                ? res.json().then((json) => this.setState({items: json}))
                : res.text().then(text => alert(text))
            )
            .finally(() => this.setState({loading: false}))
    }

    render() {
        if (this.state.loading)
            return (
                <Dimmer active inverted>
                    <Loader size='massive'>Loading</Loader>
                </Dimmer>
            );

        const pagination = calcPagination(this.state, 9, key => this.setState({page: key}));

        return (
            <Segment basic textAlign='right'>
                {this.state.dialog}

                Order by:
                <Dropdown
                    inline
                    floated='left'
                    style={{margin: "10px"}}
                    value={this.state.order}
                    options={this.orderOptions}
                    onChange={(e, data) => this.setState({order: (data.value as string)})}
                />

                Order direction:
                <Dropdown
                    inline
                    floated='left'
                    style={{margin: "10px"}}
                    value={this.state.desc}
                    options={[{key: 1, text: "Ascending", value: false}, {key: 1, text: "Descending", value: true}]}
                    onChange={(e, data) => this.setState({desc: (data.value as boolean)})}
                />

                <Input
                    floated='right'
                    icon={{ name: 'search', circular: true, link: true }}
                    placeholder='Search...'
                    onChange={(e, data) => this.setState({filter: data.value})}
                />

                <Table celled striped selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>ID</Table.HeaderCell>
                            <Table.HeaderCell>Customer</Table.HeaderCell>
                            <Table.HeaderCell>App</Table.HeaderCell>
                            <Table.HeaderCell>Version</Table.HeaderCell>
                            <Table.HeaderCell>Expiration</Table.HeaderCell>
                            <Table.HeaderCell>Description</Table.HeaderCell>
                            <Table.HeaderCell>Create</Table.HeaderCell>
                            <Table.HeaderCell>Update</Table.HeaderCell>
                            <Table.HeaderCell>Action</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {pagination.data.map((disk: IDisk, idx) =>  <Table.Row
                            key={idx}
                            className='customer-row'
                            onClick={(e: MouseEvent) => this.setState({dialog: <DiskEditDialog
                                    item={disk}
                                    type={DialogType.edit}
                                    onClose={(update: boolean) => {
                                        this.setState({dialog: null})
                                        update && this.updateDisks();
                                    }}
                                />
                            })}
                        >
                            <Table.Cell>{disk.id}</Table.Cell>
                            <Table.Cell>{disk.customer}</Table.Cell>
                            <Table.Cell>{disk.app}</Table.Cell>
                            <Table.Cell>{disk.version}</Table.Cell>
                            <Table.Cell>{new Date(disk.expiration).toDateString()}</Table.Cell>
                            <Table.Cell>{disk.description}</Table.Cell>
                            <Table.Cell>{new Date(disk.create).toLocaleString()}</Table.Cell>
                            <Table.Cell>{disk.update ? new Date(disk.update).toLocaleString() : "none"}</Table.Cell>
                            <Table.Cell collapsing textAlign="center" className="customer-action-cell" onClick={(e: MouseEvent) => e.stopPropagation()}>
                                <Icon
                                    link
                                    name="remove"
                                    onClick={(e: MouseEvent) => {
                                        e.stopPropagation();
                                        this.setState({dialog: <DeleteDialog
                                                id={disk.id}
                                                label={disk.id}
                                                uri="disk"
                                                onClose={(update: boolean) => {
                                                    this.setState({dialog: null})
                                                    update && this.updateDisks();
                                                }}
                                            />
                                        })
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>

                    {pagination.ctrl}

                </Table>
            </Segment>
        )
    }
}